import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal, Popup } from "semantic-ui-react";

import { ReactTable } from "components";
import { useComponentColumns } from "components/ROB/components/ROBServiceRequest/columns";
import "components/ROB/components/ROBServiceRequest/ROBServiceRequest.scss";
import { useROBServiceRequestQuery } from "components/ROB/hooks";
import { openNewTabURL } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { Appointment, DealerLocation } from "models";

type ROBServiceRequestProps = {
  appointment: Appointment;
  appointmentLocation: DealerLocation;
};

const STATUS_COLORS: Record<string, string | undefined> = {
  Approved: "green",
  ProvisionallyApproved: "orange",
  Denied: "red"
};

export const ROBServiceRequest = ({ appointment, appointmentLocation }: ROBServiceRequestProps) => {
  const { t } = useTranslation();

  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data: serviceRequest } = useROBServiceRequestQuery(appointment.car_id, appointmentLocation.is_robnet_enabled);
  const componentColumns = useComponentColumns();

  const toggleModal = () => setIsModalVisible(prev => !prev);

  if (!serviceRequest || !appointment.wo_nr || serviceRequest.appointment.workOrderNumber !== appointment.wo_nr) return null;

  return (
    <div className="ROBServiceRequest">
      <Popup
        disabled={!serviceRequest.serviceRequestNumber}
        content={
          <div className="request_popup">
            <div>{`${t("v8_status", "Status")}: ${serviceRequest.status}`}</div>
            <div>{`${t("v8_service_request_number", "Service request number")}: ${serviceRequest.serviceRequestNumber}`}</div>
          </div>
        }
        trigger={
          <Button className={`${STATUS_COLORS[serviceRequest.status] ?? "gray"} -appointment-status`} size="small" onClick={toggleModal}>
            {t("v8_rob_request", "ROB Request")}
          </Button>
        }
      />

      <Modal open={isModalVisible} onClose={toggleModal} closeOnDimmerClick={false} className="ROBServiceRequestModal" size="large" dimmer="blurring">
        <Modal.Header className="title_header">
          <div>{t("v8_rob_request", "ROB Request")}</div>
          <div className="header_actions">
            <Button basic icon disabled={!serviceRequest.serviceRequestURL} onClick={() => openNewTabURL(serviceRequest.serviceRequestURL)} className="portal_button">
              <Icon className="arrow up right from square" />
              <span>{t("v8_view_edit_in_rob_portal", "View / Edit in ROB Portal")}</span>
            </Button>
            <Button basic onClick={toggleModal} className="close_button">
              <Icon className="xmark grey" size="big" />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          <ReactTable columns={componentColumns} data={serviceRequest.components} emptyRowMessage={t("v8_no_data_available", "No data available")} />
          <div className="total-amount-container">
            <p>
              {`${t("v8_approved_total", "Approved total")}:`} <strong>{`€ ${formatPrice(serviceRequest.invoicing.totalAmountApproved)}`}</strong>
            </p>
          </div>

          {!!serviceRequest.remarks.supplier && (
            <div className="remark_row">
              <div className="remark_title">{t("v8_supplier_comments", "Supplier comments")}</div>
              <div className="remark_value">{serviceRequest.remarks.supplier}</div>
            </div>
          )}

          {!!serviceRequest.remarks.lessor && (
            <div className="remark_row">
              <div className="remark_title">{t("v8_leasing_company_comments", "Leasing company comments")}</div>
              <div className="remark_value">{serviceRequest.remarks.lessor}</div>
            </div>
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
};
